import type { AnalysisSubject, AnyDuringEslintMigration } from 'venn-utils';
import { Dates, FREQUENCY_TO_ENUM, getAnalysisRequest } from 'venn-utils';
import { useEffect, useState } from 'react';
import type { FrequencyEnum, RangeDebugResponse } from 'venn-api';
import { analysis } from 'venn-api';
import { isNil, compact, isEqual } from 'lodash';

export const getSubjectRange = (start?: number, end?: number, frequency?: FrequencyEnum, loading?: boolean): string => {
  if (loading) {
    return '';
  }
  if (!isNil(start) && !isNil(end) && !isNil(frequency)) {
    return `${Dates.toDDMMMYYYY(start, frequency, true)} - ${Dates.toDDMMMYYYY(end, frequency, true)}`;
  }
  return '-- - --';
};

const useSubjectAvailableRange = (subjectIn: AnalysisSubject | undefined) => {
  const [start, setStart] = useState<number | undefined>();
  const [end, setEnd] = useState<number | undefined>();
  const [frequency, setFrequency] = useState<FrequencyEnum | undefined>();
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeDebug, setRangeDebug] = useState<RangeDebugResponse | undefined>();
  const [subject, setSubject] = useState<AnalysisSubject | undefined>(subjectIn);

  useEffect(() => {
    if (isEqual(subjectIn, subject)) {
      return;
    }
    setSubject(subjectIn);
  }, [setSubject, subject, subjectIn]);

  useEffect(() => {
    const fetchRange = async () => {
      setRangeLoading(true);
      const analysisRequest = getAnalysisRequest(
        compact(['RANGE_ANALYSIS', subject?.portfolio?.historical && 'RANGE_DEBUG']),
        subject,
        {},
        false,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      );
      try {
        const { content } = await analysis(analysisRequest);
        const rangeDebug = content?.analyses?.[1]?.rangeDebug?.[0];
        setStart(content.maxStartTime);
        setEnd(content.maxEndTime);
        setFrequency(content.maxFrequency);
        setRangeDebug(rangeDebug);
      } catch (e) {
        setStart(undefined);
        setEnd(undefined);
        setFrequency(undefined);
      }
      setRangeLoading(false);
    };
    if (subject?.type === 'investment' && subject.fund) {
      setStart(subject.fund.startRange);
      setEnd(subject.fund.endRange);
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      setFrequency(FREQUENCY_TO_ENUM[subject.fund.frequency]);
      return;
    }
    if (subject?.type === 'portfolio') {
      // When the portfolio has been added from search, it doesn't have the proper Portfolio shape.
      const { startRange, endRange, frequency: portfolioFrequency } = subject.portfolio as AnyDuringEslintMigration;
      if (!isNil(startRange) && !isNil(endRange) && !isNil(portfolioFrequency)) {
        setStart(startRange);
        setEnd(endRange);
        setFrequency(portfolioFrequency);
        return;
      }
    }
    fetchRange();
  }, [subject]);

  return {
    rangeLoading,
    formattedRange: getSubjectRange(start, end, frequency === 'QUARTERLY' ? 'MONTHLY' : frequency, rangeLoading),
    end,
    rangeDebug,
  };
};

export default useSubjectAvailableRange;
